/**
 * "AI Agents" nav icon.
 *
 * Exported from the Kim Design System Figma file (node 1650:375, layer `AI Agents`) rather than
 * matched to a lucide glyph. Figma's export ships two artefacts that are stripped here: a #1E1E1E
 * canvas rect and a frame-sized white background path. The star's drop-shadow filter is dropped
 * too — it does not read at 20px.
 *
 * Inline rather than an <img> (as ShadowAiIcon does) so the stroke follows currentColor and the
 * icon dims with the rest of the nav item when inactive.
 */
export function AiAgentsIcon({ className }: { className?: string }) {
	return (
		<svg
			/*
			 * Sized by MEASURING the rendered ink, not by arithmetic on the Figma frame.
			 *
			 * The sidebar boxes icons at [&>svg]:size-5 (20px), so apparent size is decided by how much
			 * of the viewBox the glyph actually covers. Two earlier attempts got this wrong: the Figma
			 * export's 24x26 frame letterboxed in a square slot, and the 29.03 square that replaced it
			 * was still padded to the frame rather than the glyph, rendering 15.2px against lucide's
			 * 16.7px. Computing it from the path data did not settle it either — those bounds come from
			 * curve control points, which overshoot the real curve.
			 *
			 * So this is calibrated: lucide Home, Shield and a circle glyph all render exactly 16.7px
			 * of ink at size-5, and with this viewBox so does this one. The glyph is taller than it is
			 * wide (the star sits above the bot), so height is the axis that has to match — matching
			 * width would push it out of the box vertically.
			 *
			 * stroke-width tracks the viewBox at lucide's 2-in-24 ratio; leaving it fixed while the
			 * viewBox changed would have made the outline read heavier or thinner than its neighbours.
			 */
			viewBox='-0.496 0.141 26.39 26.39'
			fill='none'
			xmlns='http://www.w3.org/2000/svg'
			className={className}
			aria-hidden='true'
		>
			<path d="M11.4581 19.9893V23.7371H4.91382L5.90412 19.6766C6.10953 18.8465 6.09995 17.9814 5.87621 17.1556C5.54515 15.7881 4.91382 15.5559 4.91382 12.9964C4.9202 11.6795 5.47709 10.419 6.46199 9.49205C7.44689 8.56515 8.77912 8.04782 10.1656 8.05389C11.5521 8.05995 12.8792 8.5889 13.8551 9.52437C14.831 10.4598 15.3756 11.7252 15.3692 13.0421V18.2525C15.3237 18.757 15.088 19.229 14.7054 19.5817C14.3227 19.9343 13.819 20.144 13.2866 20.1721L11.4581 19.9893Z" stroke="currentColor" strokeWidth="2.20" strokeLinecap="round" strokeLinejoin="round"/>
			<path d="M17.731 1.76514C17.7386 1.7277 17.7921 1.7277 17.7997 1.76514C18.1969 3.70802 19.7152 5.22636 21.6581 5.62347C21.6955 5.63112 21.6955 5.68461 21.6581 5.69226C19.7152 6.08937 18.1969 7.60772 17.7997 9.5506C17.7921 9.58803 17.7386 9.58803 17.731 9.5506C17.3338 7.60772 15.8155 6.08937 13.8726 5.69226C13.8352 5.68461 13.8352 5.63112 13.8726 5.62347C15.8155 5.22636 17.3338 3.70802 17.731 1.76514Z" fill="currentColor"/>
		</svg>
	);
}
